import { useCallback, useRef, useState, useEffect } from 'react';

const SOUND_ENABLED_KEY = 'kh_sound_enabled';

export function useSounds() {
  const audioContextRef = useRef<AudioContext | null>(null);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const stored = localStorage.getItem(SOUND_ENABLED_KEY);
    return stored === null ? true : stored === 'true';
  });

  useEffect(() => {
    localStorage.setItem(SOUND_ENABLED_KEY, String(soundEnabled));
  }, [soundEnabled]);

  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    // Resume if suspended (browser requires user interaction)
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
    return audioContextRef.current;
  }, []);

  // Initialize AudioContext on first user interaction to avoid the toggle-twice issue
  useEffect(() => {
    const initAudioOnInteraction = () => {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume();
      }
      // Remove listener after first interaction
      document.removeEventListener('click', initAudioOnInteraction);
    };
    document.addEventListener('click', initAudioOnInteraction);
    return () => document.removeEventListener('click', initAudioOnInteraction);
  }, []);

  const playTone = useCallback((
    frequency: number,
    startTime: number,
    duration: number,
    volume: number = 0.3,
    type: OscillatorType = 'sine'
  ) => {
    const ctx = getAudioContext();
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.frequency.value = frequency;
    oscillator.type = type;

    gainNode.gain.setValueAtTime(0, startTime);
    gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);

    oscillator.start(startTime);
    oscillator.stop(startTime + duration);
  }, [getAudioContext]);

  const playCorrect = useCallback(() => {
    if (!soundEnabled) return;
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    // 2-tone ascending: C5 -> E5
    playTone(523.25, now, 0.12, 0.25);        // C5
    playTone(659.25, now + 0.08, 0.15, 0.3);  // E5
  }, [soundEnabled, getAudioContext, playTone]);

  // Row B: E5 → G5 → C6 (triumphant)
  const playComplete = useCallback(() => {
    if (!soundEnabled) return;
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    playTone(659.25, now, 0.15, 0.25);        // E5
    playTone(783.99, now + 0.1, 0.15, 0.25);  // G5
    playTone(1046.50, now + 0.2, 0.35, 0.35); // C6
  }, [soundEnabled, getAudioContext, playTone]);

  // Row C: Victory chord burst (for perseverance)
  const playCheer = useCallback(() => {
    if (!soundEnabled) return;
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    playTone(523.25, now, 0.4, 0.2);          // C5
    playTone(659.25, now, 0.4, 0.2);          // E5
    playTone(783.99, now, 0.4, 0.2);          // G5
    playTone(1046.50, now, 0.4, 0.2);         // C6
    playTone(1318.51, now + 0.2, 0.08, 0.15); // E6
    playTone(1567.98, now + 0.25, 0.08, 0.15);// G6
    playTone(2093.00, now + 0.3, 0.3, 0.25);  // C7
  }, [soundEnabled, getAudioContext, playTone]);

  // Row B: G4 → E4 (lower, gentle)
  const playIncorrect = useCallback(() => {
    if (!soundEnabled) return;
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    playTone(392.00, now, 0.18, 0.15, 'triangle');        // G4
    playTone(329.63, now + 0.14, 0.22, 0.18, 'triangle'); // E4
  }, [soundEnabled, getAudioContext, playTone]);

  const toggleSound = useCallback(() => {
    setSoundEnabled(prev => !prev);
  }, []);

  return {
    soundEnabled,
    toggleSound,
    playCorrect,
    playComplete,
    playCheer,
    playIncorrect
  };
}
